import { SEARCH_KIND_ICON, type SearchHit } from '@kovix/api-client';
import { useI18n, useT } from '@kovix/shell-kit/i18n';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/shared/lib/api';
import { ShellIcon } from './NavIcon';
import { shellText } from './shellText';

/**
 * Tìm kiếm toàn cục trên topbar (W11 — search-svc/OpenSearch): gõ ≥ 2 ký tự, debounce 250 ms, kết quả theo nhóm (đã lọc capability ở máy chủ),
 * ↑/↓/Enter điều hướng tới `route`. Phím `/` nhảy vào ô; Ctrl K mở command palette chức năng (mockup v150 — X0‑e), nút ⌘ trong ô cũng mở palette.
 * Không cache theo người dùng ngoài TanStack Query (staleTime ngắn).
 */
export function GlobalSearch({ onOpenPalette }: { onOpenPalette?: (() => void) | undefined }) {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const { data, isFetching, error } = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => api.search.search(debounced, undefined, 30, signal).then((r) => r.data),
    enabled: debounced.length >= 2,
    staleTime: 10_000,
    retry: 0,
  });
  const flat: SearchHit[] = data?.groups.flatMap((g) => g.hits) ?? [];
  const go = (h: SearchHit) => {
    setOpen(false);
    setQ('');
    navigate({ to: h.route as '/' });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && flat[active]) {
      e.preventDefault();
      go(flat[active] as SearchHit);
    } else if (e.key === 'Escape') {
      setOpen(false);
      input.current?.blur();
    }
  };
  let idx = -1;
  return (
    <div className="kx-search kx-shell-search">
      {/* v152 `.search.command-search`: kính lúp trái, ô tìm dữ liệu, nút "Ctrl K" bên phải mở palette chức năng */}
      <ShellIcon name="search" size={16} className="si" />
      <input
        ref={input}
        className="kx-search-input"
        role="combobox"
        aria-expanded={open}
        aria-controls="kx-search-list"
        aria-label={t('shell.search')}
        placeholder={shellText('searchPlaceholder', locale)}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
      />
      {onOpenPalette ? (
        <button
          type="button"
          className="kx-search-kbd"
          title={t('shell.commandPalette')}
          aria-label={`Ctrl K — ${t('shell.commandPalette')}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onOpenPalette}
        >
          Ctrl K
        </button>
      ) : null}
      {open && debounced.length >= 2 ? (
        <div
          id="kx-search-list"
          role="listbox"
          style={{
            position: 'absolute',
            top: 46,
            left: 0,
            right: 0,
            zIndex: 50,
            background: 'var(--kx-bg, #fff)',
            border: '1px solid var(--kx-line-2)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
            maxHeight: 420,
            overflowY: 'auto',
          }}
        >
          {error ? (
            <div className="kx-cellsub" style={{ padding: 10 }}>
              {t('common.error')}
            </div>
          ) : null}
          {!error && !isFetching && flat.length === 0 ? (
            <div className="kx-cellsub" style={{ padding: 10 }}>
              {t('shell.searchEmpty')}
            </div>
          ) : null}
          {data?.groups.map((g) => (
            <div key={g.kind}>
              <div className="kx-nav-group" style={{ padding: '6px 10px 2px' }}>
                {SEARCH_KIND_ICON[g.kind]} {t(`search.kind.${g.kind}`)}
              </div>
              {g.hits.map((h) => {
                idx += 1;
                const i = idx;
                return (
                  // biome-ignore lint/a11y/useFocusableInteractive: listbox điều khiển từ ô tìm (↑↓/Enter) — focus giữ ở ô nhập, mục không nhận focus
                  <div
                    key={`${h.kind}-${h.id}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      go(h);
                    }}
                    onMouseEnter={() => setActive(i)}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      background: i === active ? 'var(--kx-blue-50, rgba(0,0,0,.05))' : undefined,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span>
                      <b>{h.title}</b>
                      {h.subtitle ? <span className="kx-cellsub"> · {h.subtitle}</span> : null}
                    </span>
                    <span className="kx-cellsub">
                      {h.code}
                      {h.status ? ` · ${h.status}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
          {isFetching ? (
            <div className="kx-cellsub" style={{ padding: '4px 10px' }}>
              {t('common.loading')}
            </div>
          ) : null}
        </div>
      ) : open && q.length > 0 && q.trim().length < 2 ? (
        <div className="kx-cellsub" style={{ position: 'absolute', top: 46, left: 0, padding: 6 }}>
          {t('shell.searchHint')}
        </div>
      ) : null}
    </div>
  );
}
